import type { IDataAccessor } from "./data-accessor";
import type { IPaging, IResponse } from "./model";

type ResponseType<T> = angular.IPromise<IResponse<T>>;
export type ParameterType = { [name: string]: any };

export abstract class AbstractAccessorService<T> {

    /**
     * 服务名称
     */
    public readonly abstract $name: string;

    /**
     * 注入的IDataAccessor
     */
    public abstract $accessor: IDataAccessor<T>;

    /**
     * 获取单个对象
     * @returns 
     */
    public get(): ResponseType<T>;
    /**
     * 获取单个对象
     * @param {S} parameter 参数
     * 
     * @template {any} S
     * 
     * @returns {angular.IPromise<IResponse<T>>}
     */
    public get<S extends ParameterType>(parameter: S): ResponseType<T>;
    public get<S extends ParameterType>(parameter?: S): ResponseType<T> {
        return this.$accessor.get(this.$name, parameter);
    }

    /**
     * 根据id获取单个对象
     * @param {(number | string)} id
     * @returns 
     */
    public getOne(id: number | string): ResponseType<T> {
        return this.$accessor<T, any, string>(this.$name, "getOne", { "parameter": { "id": id } });
    }

    /**
     * 获取列表（不分页）
     */
    public getAll(): ResponseType<Array<T>>;
    public getAll<S extends ParameterType>(parameter: S): ResponseType<Array<T>>;
    public getAll<S extends ParameterType>(parameter?: S): ResponseType<Array<T>> {
        return this.$accessor<Array<T>, any, string>(this.$name, "getAll", { "parameter": parameter });
    }

    /**
     * 获取列表（分页）
     * @param parameter 
     * @returns 
     */
    public query<S extends ParameterType>(parameter: S): ResponseType<IPaging<T>> {
        return this.$accessor.query(this.$name, parameter);
    }

    /**
     * 添加对象
     * @param data 
     * @param parameters 
     * @returns 
     */
    public save(data: T): ResponseType<boolean & T>;
    public save<S extends ParameterType>(data: T, parameters: S): ResponseType<boolean | T>;
    public save<S extends ParameterType>(data: T, parameters?: S): ResponseType<boolean | T> {
        return this.$accessor.save(this.$name, data, parameters);
    }

    /**
     * 根据id保存单个对象
     * @param {(number | string)} id
     * @returns 
     */
    public saveOne(data: T, id: number | string): ResponseType<boolean> {
        return this.$accessor<boolean, any, string>(this.$name, "saveOne", { "parameter": { "id": id }, "data": data });
    }

    /**
     * 修改对象
     * @param data 
     * @param parameters 
     */
    public update(data: T): ResponseType<boolean & T>;
    public update<S extends ParameterType>(data: T, parameters: S): ResponseType<boolean & T>;
    public update<S extends ParameterType>(data: T, parameters?: S): ResponseType<boolean & T> {
        return this.$accessor.update(this.$name, data, parameters);
    }

    /**
     * 根据id修改单个对象
     * @param {(number | string)} id
     * @returns 
     */
    public updateOne(data: T, id: number | string): ResponseType<boolean> {
        return this.$accessor<boolean, any, string>(this.$name, "updateOne", { "parameter": { "id": id }, "data": data });
    }

    /**
     * 删除对象
     * @param parameters 
     */
    public delete(): ResponseType<boolean>;
    public delete<S extends ParameterType>(parameters: S): ResponseType<boolean>;
    public delete<S extends ParameterType>(parameters?: S): ResponseType<boolean> {
        return this.$accessor.delete(this.$name, parameters);
    }

    /**
     * 根据id删除单个对象
     * @param {(number | string)} id
     * @returns 
     */
    public deleteOne(id: number | string): ResponseType<boolean> {
        return this.$accessor<boolean, any, string>(this.$name, "deleteOne", { "parameter": { "id": id } });
    }
}